/**
 * Module "Navigation Tabs"
 *
 * app-config parameter
 * "navigation cosmos icon" sets an imported cosmos icon, shown in navigation tab
 * modules > [] > navigationIcon, e. g.
 *    "navigationCosmosIcon": "CosmosIconFlag",
 *
 * */

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import style from './navigation-tabs.module.scss';
import { Icon } from '../Svg/icon';
import { Modal, ModalBody, ModalHeader } from '../Modal';
import { AppContext } from '../../App';
import { getTrans } from '../../Utils/translate';
import { CosmosText } from '../../cosmos/web-scoped/react';
import { off, on } from '../../Utils/events';
import { findByMatchingProperties } from '../../Utils/array';

import { AnimatedGuide } from '../AnimatedGuide';
import { AthleteProfile } from '../AthleteProfile';
import { CheckpointResults } from '../CheckpointResults';
import { HeadToHead } from '../HeadToHead';
import { ImageWithDropDown } from '../ImageWithDropDown';
import { JudgesHeadToHead } from '../JudgesHeadToHead';
import { JudgesResults } from '../JudgesResults';
import { KnockOutTree2 } from '../KnockOutTree2';
import { KnockOutTree } from '../KnockOutTree';
import { Results } from '../Results';
import { SimpleTrack } from '../SimpleTrack';
import { SwitchableModules } from '../SwitchableModules';
import { Wipe } from '../Advertisement/wipe';
import { ComparisonLite } from '../ComparisonLite';
import { AthleteProfileLite } from '../AthleteProfileLite';
import { InteractiveTrack } from '../InteractiveTrack';
import { enrichCrosslinkConfigs, handleClickAthleteName } from '../../Utils/crosslink';

const NavigationTabs = ({ modules }) => {
  const context = useContext(AppContext);
  const { more } = context.lang;
  const [showModal, setShowModal] = useState(false);
  const [headerBounds, setHeaderBounds] = useState({ width: 0, height: 0 });
  const [option, setOption] = useState({ index: 0, type: modules[0].type });
  const allComponents = {
    animatedguide: AnimatedGuide,
    athleteprofile: AthleteProfile,
    checkpointresults: CheckpointResults,
    headtohead: HeadToHead,
    imagewithdropdown: ImageWithDropDown,
    athleteprofilelite: AthleteProfileLite,
    interactivetrack: InteractiveTrack,
    judgesheadtohead: JudgesHeadToHead,
    comparisonlite: ComparisonLite,
    judgesresults: JudgesResults,
    knockouttree2: KnockOutTree2,
    knockouttree: KnockOutTree,
    results: Results,
    simpletrack: SimpleTrack,
    switchablemodules: SwitchableModules,
    wipe: Wipe
  };

  const headerRef = useRef(null);
  const navRef = useRef(null);

  const CurrentComponent = useMemo(() => {
    let openWidget = option.type.toLowerCase();

    if (openWidget === 'judges-results') openWidget = 'judgesresults';

    return allComponents[openWidget];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option]);

  const handleChange = (index) => {
    setOption({ type: modules[index].type, index });
  };

  // sets count of visible navigation tab items
  const maxVisibleItems = 5;
  const itemWidth = 70;
  const visibleItems =
    window.innerWidth / itemWidth < maxVisibleItems
      ? Math.floor(window.innerWidth / itemWidth)
      : maxVisibleItems;

  // calculate the additional more tab in item slicing, if needed
  const length = modules.length > visibleItems ? visibleItems - 1 : modules.length;

  // change component by type, based on events
  const onSwitchComponent = (event) => {
    // find modules with matching conditions
    let matches = [];
    event.detail.targets.map((_module) => {
      const _matches = findByMatchingProperties(modules, _module.condition);
      if (_matches.length) {
        matches.push({
          modules: _matches,
          config: _module.config,
          targets: _module.targets
        });
      }
      return true;
    });
    // use first match
    const index = matches.length > 0 ? modules.indexOf(matches[0].modules[0]) : -1;

    if (index > -1) {
      // override config
      context.config.modules[index] = {
        ...context.config.modules[index],
        ...matches[0].config
      };
      // change module in frontend
      handleChange(index);
      // trigger nested event if defined, eg. for component SwitchableModules
      if (matches[0].targets) {
        const match = enrichCrosslinkConfigs(
          'initiateWithAthleteId',
          matches[0].config.initiateWithAthleteId,
          { targets: matches[0].targets }
        );
        // @todo solve race conditions
        setTimeout(() => {
          handleClickAthleteName(
            new Event(''),
            matches[0].config.initiateWithAthleteId,
            match
          );
        }, 200);
      }
    }
  };

  useEffect(() => {
    if (showModal) {
      // init UI
      setHeaderBounds({
        width: headerRef && headerRef.current ? headerRef.current.clientWidth : 0,
        height: headerRef && headerRef.current ? headerRef.current.clientHeight : 0
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  useEffect(() => {
    context.navBounds = {
      width: navRef && navRef.current ? navRef.current.clientWidth : 0,
      height: navRef && navRef.current ? navRef.current.clientHeight : 0
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navRef.current]);

  useEffect(() => {
    // listen to component switch events
    on('switchComponent', onSwitchComponent);
    return () => {
      off('switchComponent', onSwitchComponent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`navWrapper ${style.navWrapper}`}>
      <div>
        <nav
          ref={navRef}
          className={`${style.mediaXs} ${style.navigationTabs} ${
            modules.length < 4 ? style.smallNav : ''
          }`}
        >
          {modules &&
            modules.slice(0, length).map((singleModule, index) => {
              if (!singleModule.type)
                return <div key={`error-${index}`}>No module type given</div>;

              const isActive = modules[option.index] === singleModule ? style.active : '';

              const title = singleModule.navigationTitle
                ? getTrans(singleModule.navigationTitle)
                : singleModule.type;

              return (
                <div
                  className={style.linkWrapper}
                  title={title}
                  key={singleModule.type.toLowerCase() + index}
                >
                  <div
                    onClick={() => {
                      handleChange(index);
                    }}
                    className={`${style.navItem} ${isActive}`}
                  >
                    <Icon
                      className={`${style.navItem__icon} ${isActive}`}
                      cosmosIcon={`${singleModule.navigationCosmosIcon || ''}`}
                      name={`${singleModule.navigationIcon || ''}`}
                    />
                    <CosmosText
                      size="x-small"
                      weight="medium"
                      clamp="1"
                      class={`${style.navItem__label} ${isActive}`}
                    >
                      {title}
                    </CosmosText>
                  </div>
                </div>
              );
            })}
          {modules.length > visibleItems && (
            <div className={style.linkWrapper}>
              <div
                onClick={() => {
                  setShowModal(true);
                }}
                className={`${style.navItem}`}
              >
                <Icon
                  className={`${style.navItem__icon}`}
                  cosmosIcon="CosmosIconGenericContent"
                />
                <CosmosText
                  size="x-small"
                  weight="medium"
                  class={`${style.navItem__label}`}
                >
                  {getTrans(more) || 'More'}
                </CosmosText>
              </div>
            </div>
          )}
        </nav>
      </div>
      <Modal show={showModal} setShow={setShowModal} layout="bare">
        <ModalHeader ref={headerRef} />
        <ModalBody>
          <PerfectScrollbar
            style={{ height: `calc(100vh - ${headerBounds.height}px)` }}
            options={{ wheelPropagation: true }}
          >
            {modules &&
              modules.map((singleModule, index) => {
                // skip visible tab items for modal list
                if (index < length) {
                  return '';
                }
                const isActive =
                  modules[option.index] === singleModule ? style.modalItemActive : '';
                return (
                  <div
                    className={style.linkWrapper}
                    key={singleModule.type.toLowerCase() + index}
                  >
                    <CosmosText
                      onClick={() => {
                        handleChange(index);
                        setShowModal(false);
                      }}
                      class={`${style.modalItem} ${isActive}`}
                    >
                      {singleModule.navigationTitle
                        ? getTrans(singleModule.navigationTitle)
                        : singleModule.type}
                    </CosmosText>
                  </div>
                );
              })}
          </PerfectScrollbar>
        </ModalBody>
      </Modal>
      <PerfectScrollbar
        style={{ height: 'calc(100vh - 60px)', overflow: 'hidden' }}
        options={{ wheelPropagation: true }}
      >
        <CurrentComponent
          key={`module-${option.index}-${modules[
            option.index
          ].type.toLowerCase()}-${modules[option.index].navigationTitle.toLowerCase()}`}
          config={modules[option.index]}
        />
      </PerfectScrollbar>
    </div>
  );
};

export { NavigationTabs };
